import { Services } from '~publish/legacy/constants/services'

import { withCopiedSearchParams } from './helpers/withCopiedSearchParams'

type GlobalPostFilters = {
  channelIds?: string[]
  tagIds?: string[]
  /**
   * Post formats. Single-channel routes only — the formats depend on the
   * channel's service. @see supportedPostTypesForService
   */
  postTypes?: string[]
}

export const DEFAULT_TAB_ID = 'queue'

// Profiles routes
export const profileServicePages = {
  route: `/profile/:service(${Services.map((service) => service.name).join(
    '|',
  )})`,
}
export const profileServiceWithTabPages = {
  route: `/profile/:service(${Services.map((service) => service.name).join(
    '|',
  )})/tab/:tabId(queue|sentPosts|drafts|settings|approvals)`,
}
export const profilePages = {
  route: '/profile/:profileId',
  getRoute: ({ profileId }: { profileId: string }): string =>
    `/profile/${profileId}`,
}

export const profileTabPages = {
  route: '/profile/:profileId/tab/:tabId',
  getRoute: ({
    profileId,
    tabId = 'queue',
  }: {
    profileId: string
    tabId?: string
  }): string => `/profile/${profileId}/tab/${tabId}`,
  defaultRoute: '/profile/:profileId/tab/queue',
}

export const profileChildTabPages = {
  route: '/profile/:profileId/tab/:tabId/:childTabId?',
  getRoute: ({
    profileId,
    tabId,
    childTabId,
  }: {
    profileId: string
    tabId: string
    childTabId: string
  }): string => `/profile/${profileId}/tab/${tabId}/${childTabId}`,
}

// Miscellaneous routes
export const generic = {
  route: '/',
}

// Preferences routes
export const preferencesPage = {
  route: '/preferences',
}

export const preferencesAppsExtras = {
  route: '/preferences/appsandextras',
}

export const preferencesSecurity = {
  route: '/preferences/security',
}

export const preferencesGeneral = {
  route: '/preferences/general',
}

export const preferencesNotifications = {
  route: '/preferences/notifications',
}

// Campaigns routes
export const campaignsPage = {
  route: '/campaigns',
}

export const tagsPage = {
  route: '/tags',
}
export const campaignCreate = {
  route: '/campaigns/new',
}

export const channelGroups = {
  route: '/channel-groups',
}

const commonChannelFilterParams = ['channels[]', 'tags[]'] as const
const getChannelFilterParams = (forwardParams?: boolean): string[] =>
  forwardParams ? [...commonChannelFilterParams] : []

export const allChannels = {
  route: '/all-channels',
  queryParams: [
    { param: 'tab', values: ['queue', 'sent', 'drafts', 'approvals'] },
    { param: 'channels', values: undefined },
    { param: 'tags', values: undefined },
  ],
  buildPathname: (): string => '/all-channels',
  getRoute: (filters?: GlobalPostFilters & { tab?: string }): string =>
    withCopiedSearchParams(
      '/all-channels',
      [],
      [
        ...(filters?.channelIds?.map(
          (channelId) => ['channels[]', channelId] satisfies [string, string],
        ) ?? []),
        ...(filters?.tagIds?.map(
          (tagId) => ['tags[]', tagId] satisfies [string, string],
        ) ?? []),
        ...((filters?.tab ? [['tab', filters.tab]] : []) satisfies [
          string,
          string,
        ][]),
      ],
    ),
}

// New IA Overhaul route (CORE-3371) - schedule list view
export const schedule = {
  route: '/schedule',
  queryParams: [
    { param: 'tab', values: ['queue', 'sent', 'drafts', 'approvals'] },
    { param: 'channels', values: undefined },
    { param: 'tags', values: undefined },
  ],
  buildPathname: (): string => '/schedule',
  getRoute: (filters?: GlobalPostFilters & { tab?: string }): string =>
    withCopiedSearchParams(
      '/schedule',
      [],
      [
        ...(filters?.channelIds?.map((channelId): [string, string] => [
          'channels[]',
          channelId,
        ]) ?? []),
        ...(filters?.tagIds?.map((tagId): [string, string] => [
          'tags[]',
          tagId,
        ]) ?? []),
        ...(filters?.tab
          ? [['tab', filters.tab] satisfies [string, string]]
          : []),
      ],
    ),
}

// New IA Overhaul routes (CORE-3371) - schedule post routes
export const schedulePostNew = {
  route: '/schedule/new',
  getRoute: (): string => '/schedule/new',
}

export const newCalendarAllChannels = {
  route: '/all-channels/calendar/:granularity',
  queryParams: [
    { param: 'channels', values: undefined },
    { param: 'tags', values: undefined },
  ],
  buildPathname: (params: {
    granularity: 'week' | 'month' | 'three-day'
  }): string => `/all-channels/calendar/${params.granularity}`,

  getRoute: (
    granularity: 'week' | 'month' | 'three-day',
    filters?: GlobalPostFilters,
    forwardParams?: boolean,
  ): string =>
    withCopiedSearchParams(
      `/all-channels/calendar/${granularity}`,
      [...getChannelFilterParams(forwardParams), 'date'],
      [
        ...(filters?.channelIds?.map(
          (channelId) => ['channels[]', channelId] satisfies [string, string],
        ) ?? []),
        ...(filters?.tagIds?.map(
          (tagId) => ['tags[]', tagId] satisfies [string, string],
        ) ?? []),
      ],
    ),
}

// New IA Overhaul routes (CORE-3371)
export const scheduleCalendar = {
  route: '/schedule/calendar/:granularity',
  queryParams: [
    { param: 'channels', values: undefined },
    { param: 'tags', values: undefined },
  ],
  buildPathname: (params: {
    granularity: 'week' | 'month' | 'three-day'
  }): string => `/schedule/calendar/${params.granularity}`,

  getRoute: (
    granularity: 'week' | 'month' | 'three-day',
    filters?: GlobalPostFilters,
    forwardParams?: boolean,
  ): string =>
    withCopiedSearchParams(
      `/schedule/calendar/${granularity}`,
      [...getChannelFilterParams(forwardParams), 'date'],
      [
        ...(filters?.channelIds?.map((channelId): [string, string] => [
          'channels[]',
          channelId,
        ]) ?? []),
        ...(filters?.tagIds?.map((tagId): [string, string] => [
          'tags[]',
          tagId,
        ]) ?? []),
      ],
    ),
}

// New IA Overhaul route (CORE-3371) - schedule board view. Board owns its
// own `board*`-prefixed filter params (@see useBoardState), so unlike
// `schedule`/`scheduleCalendar` this route has no filters of its own to
// carry over.
export const scheduleBoard = {
  route: '/schedule/board',
  buildPathname: (): string => '/schedule/board',
  getRoute: (): string => '/schedule/board',
}

export const channel = {
  route: '/channels/:id',
  queryParams: [
    {
      param: 'tab',
      values: ['queue', 'sent', 'drafts', 'approvals'],
    },
    { param: 'tags', values: undefined },
    { param: 'postTypes', values: undefined },
  ] as const,
  buildPathname: (params: { id: string }): string => `/channels/${params.id}`,
  getRoute: (
    channelId: string,
    filters: Pick<GlobalPostFilters, 'tagIds' | 'postTypes'> & {
      tab?: string
    } = {},
  ): string =>
    withCopiedSearchParams(
      `/channels/${channelId}`,
      [],
      [
        !!filters.tab && ['tab', filters.tab],
        ...(filters.tagIds?.map(
          (tagId) => ['tags[]', tagId] satisfies [string, string],
        ) ?? []),
        ...(filters.postTypes?.map((postType): [string, string] => [
          'postTypes[]',
          postType,
        ]) ?? []),
      ],
    ),
}

// New IA Overhaul route (CORE-3371) - channel schedule list view
export const channelSchedule = {
  route: '/channels/:id/schedule',
  queryParams: [
    {
      param: 'tab',
      values: ['queue', 'sent', 'drafts', 'approvals'],
    },
    { param: 'tags', values: undefined },
    { param: 'postTypes', values: undefined },
  ] as const,
  buildPathname: (params: { id: string }): string =>
    `/channels/${params.id}/schedule`,
  getRoute: (
    channelId: string,
    filters: Pick<GlobalPostFilters, 'tagIds' | 'postTypes'> & {
      tab?: string
    } = {},
  ): string =>
    withCopiedSearchParams(
      `/channels/${channelId}/schedule`,
      [],
      [
        !!filters.tab && ['tab', filters.tab],
        ...(filters.tagIds?.map((tagId): [string, string] => [
          'tags[]',
          tagId,
        ]) ?? []),
        ...(filters.postTypes?.map((postType): [string, string] => [
          'postTypes[]',
          postType,
        ]) ?? []),
      ],
    ),
}

// New IA Overhaul route (CORE-3371) - channel schedule board view
export const channelScheduleBoard = {
  route: '/channels/:id/schedule/board',
  buildPathname: (params: { id: string }): string =>
    `/channels/${params.id}/schedule/board`,
  getRoute: (channelId: string): string =>
    `/channels/${channelId}/schedule/board`,
}

// New IA Overhaul routes (CORE-3371) - channel schedule post routes
export const channelSchedulePostNew = {
  route: '/channels/:id/schedule/new',
  getRoute: ({ channelId }: { channelId: string }): string =>
    `/channels/${channelId}/schedule/new`,
}

export const channelSchedulePostDetails = {
  route: '/channels/:id/schedule/:postId',
  getRoute: ({
    channelId,
    postId,
  }: {
    channelId: string
    postId: string
  }): string => `/channels/${channelId}/schedule/${postId}`,
}

export const channelSchedulePostEdit = {
  route: '/channels/:id/schedule/:postId/edit',
  getRoute: ({
    channelId,
    postId,
  }: {
    channelId: string
    postId: string
  }): string => `/channels/${channelId}/schedule/${postId}/edit`,
}

export const postEditRoute = {
  route: '/posts/:postId/edit',
  getRoute: ({ postId }: { postId: string }): string => `/posts/${postId}/edit`,
}

export const postDetailsRoute = {
  route: '/posts/:postId',
  getRoute: ({ postId }: { postId: string }): string => `/posts/${postId}`,
}

/**
 * The shareable link to a post. Same path as {@link postDetailsRoute}, but that
 * one is only ever appended to a list view's path and never resolves alone. This
 * is mounted at the top level, so it is the form that survives being pasted.
 */
export const postPermalinkRoute = {
  route: '/posts/:postId',
  getRoute: ({ postId }: { postId: string }): string => `/posts/${postId}`,
}

/**
 * The shareable link to a channel-less draft. Cannot reuse
 * {@link postPermalinkRoute}: a draft's id is a content item id, which
 * `post(input:)` does not resolve.
 */
export const draftPermalinkRoute = {
  route: '/content/:contentItemId',
  getRoute: ({ contentItemId }: { contentItemId: string }): string =>
    `/content/${contentItemId}`,
}

/**
 * Where a ContentItem Note mention links, with `?noteId=`. Mounted next to
 * {@link draftPermalinkRoute}, and the same link while ContentItem Notes are off.
 */
export const contentItemNotesRoute = {
  route: '/content/:contentItemId/notes',
  getRoute: ({ contentItemId }: { contentItemId: string }): string =>
    `/content/${contentItemId}/notes`,
}

/**
 * Where {@link draftPermalinkRoute} resolves to. The tab is always `drafts`: a
 * channel-less draft has no other status.
 */
export const scheduleDraftDetails = {
  route: '/schedule/drafts/:contentItemId',
  getRoute: ({ contentItemId }: { contentItemId: string }): string =>
    `/schedule/drafts/${contentItemId}?tab=drafts`,
}

export const postNewRoute = {
  route: '/posts/new',
}

export const ideaEditRoute = {
  route: '/ideas/:ideaId/edit',
  getRoute: ({ ideaId }: { ideaId: string }): string => `/ideas/${ideaId}/edit`,
}

export const ideaDetailsRoute = {
  route: '/ideas/:ideaId',
  getRoute: ({ ideaId }: { ideaId: string }): string => `/ideas/${ideaId}`,
}

export const ideaNewRoute = {
  route: '/create/ideas/new',
}

export const newCalendarSingleChannel = {
  route: '/channels/:id/calendar/:granularity' as const,
  queryParams: [
    { param: 'tags', values: undefined },
    { param: 'postTypes', values: undefined },
  ] as const,
  buildPathname: (params: {
    id: string
    granularity: 'week' | 'month' | 'three-day'
  }): string => `/channels/${params.id}/calendar/${params.granularity}`,
  getRoute: (
    channelId: string,
    granularity: 'week' | 'month' | 'three-day',
    filters: GlobalPostFilters = {},
    forwardParams?: boolean,
  ): string =>
    withCopiedSearchParams(
      `/channels/${channelId}/calendar/${granularity}`,
      [...getChannelFilterParams(forwardParams), 'date'],
      [
        ...(filters.tagIds?.map((tagId): [string, string] => [
          'tags[]',
          tagId,
        ]) ?? []),
        ...(filters.postTypes?.map((postType): [string, string] => [
          'postTypes[]',
          postType,
        ]) ?? []),
      ],
    ),
}

export const channelSettings = {
  route: '/channels/:id/settings',
  getRoute: ({
    channelId,
    tab,
  }: {
    channelId: string
    tab?: 'posting-schedule' | 'general'
  }): string => `/channels/${channelId}/settings${tab ? `?tab=${tab}` : ''}`,
}

export const channelShopGrid = {
  route: '/channels/:id/shop-grid',
}

export const tagCreate = {
  route: '/tags/new',
}

export const campaignEdit = {
  route: '/campaigns/:id/edit',
}

export const tagEdit = {
  route: '/tags/:id/edit',
}

export const tagTab = {
  route: '/tags/:id/:tab',
  getRoute: ({ tagId, tab }: { tagId: string; tab: string }): string =>
    `/tags/${tagId}/${tab}`,
}

// Content Routes
export const createPage = {
  route: '/create',
}

export const createTemplates = {
  route: '/create/templates',
}

export const templateDetails = {
  route: '/template/:templateId',
  linkTo: (
    templateId: string,
    cta?: string,
    preselectChannelId?: string,
  ): string =>
    `/template/${templateId}${cta ? `?cta=${cta}` : ''}${
      preselectChannelId ? `&preselectChannelId=${preselectChannelId}` : ''
    }`,
}

export const commentsPage = {
  route: '/comments',
  queryParams: [
    {
      param: 'status',
      values: ['all', 'unreplied', 'replied', 'dismissed', 'new'],
    },
  ] as const,
  buildPathname: (): string => '/comments',
}

export type CommentsChannelView = 'comments' | 'posts'

export const commentsChannel = {
  // We define two routes because React Router v6 doesn't support dynamic routes
  // with optional params.
  baseRoute: '/comments/:channelId',
  route: '/comments/:channelId/:view',
  postsRoute: '/comments/:channelId/by-post/:postId',
  getTabRoute: ({
    channelId,
    tab,
  }: {
    channelId: string
    tab: string
  }): string => {
    return withCopiedSearchParams(`/comments/${channelId}`, [], [['tab', tab]])
  },
  queryParams: [
    {
      param: 'tab',
      values: ['comments', 'mentions'],
    },
    {
      param: 'status',
      values: ['all', 'unreplied', 'replied', 'dismissed', 'new'],
    },
    {
      param: 'sort',
      values: ['asc', 'desc'],
    },
    {
      param: 'publishedAt',
      values: ['last7days', 'last30days', 'last6months', 'lastyear'],
    },
  ] as const,
  buildPathname: ({
    channelId,
    view,
    postId,
  }: {
    channelId: string
    view?: CommentsChannelView
    postId?: string
  }): string => {
    const basePath =
      view === 'posts' && postId
        ? `/comments/${channelId}/by-post/${postId}`
        : view === 'posts'
          ? `/comments/${channelId}/posts`
          : `/comments/${channelId}`

    return withCopiedSearchParams(basePath, [
      'tab',
      'status',
      'sort',
      'publishedAt',
    ])
  },
  getRoute: ({
    channelId,
    view,
    postId,
    copiedSearchParams,
  }: {
    channelId?: string
    view?: CommentsChannelView
    postId?: string
    copiedSearchParams?: string[]
  }): string => {
    // If no channelId provided, return base /comments route with optional view
    if (!channelId) {
      // Handle all-channels by-post view (when postId is provided)
      if (view === 'posts' && postId) {
        const basePath = `/comments/by-post/${postId}`
        return withCopiedSearchParams(
          basePath,
          copiedSearchParams ?? ['status'],
        )
      }
      // Handle all-channels posts view
      if (view === 'posts') {
        return withCopiedSearchParams(
          '/comments/posts',
          copiedSearchParams ?? ['status'],
        )
      }
      // Handle all-channels list view (default)
      return withCopiedSearchParams(
        '/comments',
        copiedSearchParams ?? ['status'],
      )
    }

    // Build path with postId if provided
    const basePath =
      view === 'posts' && postId
        ? `/comments/${channelId}/by-post/${postId}`
        : view === 'posts'
          ? `/comments/${channelId}/posts`
          : `/comments/${channelId}`

    return withCopiedSearchParams(
      basePath,
      copiedSearchParams ?? ['tab', 'status', 'sort', 'publishedAt'],
    )
  },
}

// Community routes (New IA Overhaul)
/**
 * Deep-link to a single comment, opened as a dialog over the Community page.
 * This is the canonical shareable URL for a comment (all-channels path),
 * copied by the comment-details dialog's copy-link action.
 */
export const commentDetailsRoute = {
  route: '/community/comment/:commentId',
  getRoute: ({ commentId }: { commentId: string }): string =>
    `/community/comment/${commentId}`,
}

/**
 * Deep-link to a single mention, opened as a dialog over the Community page.
 * This is the canonical shareable URL for a mention (all-channels path),
 * copied by the mention-details dialog's copy-link action.
 */
export const mentionDetailsRoute = {
  route: '/community/mention/:mentionId',
  getRoute: ({ mentionId }: { mentionId: string }): string =>
    `/community/mention/${mentionId}`,
}

export const communityPage = {
  route: '/community',
  postsRoute: '/community/posts',
  postDetailRoute: '/community/by-post/:postId',
  queryParams: [
    {
      param: 'status',
      values: ['all', 'unreplied', 'replied', 'dismissed', 'new'],
    },
    { param: 'channels', values: undefined },
  ] as const,
  buildPathname: (): string => '/community',
  /**
   * Get the route for the community page with optional view and postId.
   * - /community - list view (default)
   * - /community/posts - by post view
   * - /community/by-post/:postId - specific post view
   */
  getViewRoute: ({
    view,
    postId,
    copiedSearchParams,
  }: {
    view?: CommentsChannelView
    postId?: string
    copiedSearchParams?: string[]
  }): string => {
    const basePath =
      view === 'posts' && postId
        ? `/community/by-post/${postId}`
        : view === 'posts'
          ? '/community/posts'
          : '/community'

    return withCopiedSearchParams(
      basePath,
      copiedSearchParams ?? ['status', 'channels[]'],
    )
  },
  getRoute: (filters?: GlobalPostFilters & { status?: string }): string =>
    withCopiedSearchParams(
      '/community',
      [],
      [
        ...(filters?.channelIds?.map((channelId): [string, string] => [
          'channels[]',
          channelId,
        ]) ?? []),
        ...(filters?.status
          ? [['status', filters.status] satisfies [string, string]]
          : []),
      ],
    ),
}

export const channelCommunity = {
  /**
   * Get the route for a channel's community page.
   * Returns: /channels/:channelId/community
   */
  route: '/channels/:channelId/community',
  getRoute: (channelId: string): string => {
    return `/channels/${channelId}/community`
  },
}

export const communityChannel = {
  /**
   * Route helpers for channel community pages (New IA Overhaul).
   * Mirrors the structure of commentsChannel but uses /channels/:channelId/community paths.
   */
  baseRoute: '/channels/:channelId/community',
  route: '/channels/:channelId/community/:view',
  postsRoute: '/channels/:channelId/community/by-post/:postId',
  getTabRoute: ({
    channelId,
    tab,
  }: {
    channelId: string
    tab: string
  }): string => {
    return withCopiedSearchParams(
      `/channels/${channelId}/community`,
      [],
      [['tab', tab]],
    )
  },
  queryParams: [
    {
      param: 'tab',
      values: ['comments', 'mentions'],
    },
    {
      param: 'status',
      values: ['all', 'unreplied', 'replied', 'dismissed', 'new'],
    },
    {
      param: 'sort',
      values: ['asc', 'desc'],
    },
    {
      param: 'publishedAt',
      values: ['last7days', 'last30days', 'last6months', 'lastyear'],
    },
  ] as const,
  buildPathname: ({
    channelId,
    view,
    postId,
  }: {
    channelId: string
    view?: CommentsChannelView
    postId?: string
  }): string => {
    const basePath =
      view === 'posts' && postId
        ? `/channels/${channelId}/community/by-post/${postId}`
        : view === 'posts'
          ? `/channels/${channelId}/community/posts`
          : `/channels/${channelId}/community`

    return withCopiedSearchParams(basePath, [
      'tab',
      'status',
      'sort',
      'publishedAt',
    ])
  },
  getRoute: ({
    channelId,
    view,
    postId,
    copiedSearchParams,
  }: {
    channelId: string
    view?: CommentsChannelView
    postId?: string
    copiedSearchParams?: string[]
  }): string => {
    // Build path with postId if provided
    const basePath =
      view === 'posts' && postId
        ? `/channels/${channelId}/community/by-post/${postId}`
        : view === 'posts'
          ? `/channels/${channelId}/community/posts`
          : `/channels/${channelId}/community`

    return withCopiedSearchParams(
      basePath,
      copiedSearchParams ?? ['tab', 'status', 'sort', 'publishedAt'],
    )
  },
}

export const singleFeedPage = {
  route: '/feeds/:feedId',
  linkTo: (feedId: string): string => `/feeds/${feedId}`,
}

export const feedCollectionPage = {
  route: '/feed/collections/:feedCollectionId',
  linkTo: (feedCollectionId: string): string =>
    `/feed/collections/${feedCollectionId}`,
}

export const createNew = {
  route: ideaNewRoute.route,
}

// Organization Routes
export const organization = {
  route: '/org/:id',
  getRoute: ({ orgId }: { orgId: string }): string => `/org/${orgId}`,
}

const getCalendarRoute = (
  granularity: 'week' | 'month' | 'three-day',
  filters?: GlobalPostFilters,
  forwardParams?: boolean,
): string => {
  const { channelIds, tagIds } = filters || {}
  const channels: [string, string][] =
    channelIds?.map((channelId) => ['channels[]', channelId]) ?? []
  const tags: [string, string][] =
    tagIds?.map((tagId) => ['tags[]', tagId]) ?? []
  return withCopiedSearchParams(
    `/calendar/${granularity}`,
    getChannelFilterParams(forwardParams),
    [...channels, ...tags],
  )
}

export const calendarMonth = {
  route: '/calendar/month',
  getRoute: (filters?: GlobalPostFilters, forwardParams?: boolean): string => {
    return getCalendarRoute('month', filters, forwardParams)
  },
}

export const calendarWeek = {
  route: '/calendar/week',
  getRoute: (filters?: GlobalPostFilters, forwardParams?: boolean): string => {
    return getCalendarRoute('week', filters, forwardParams)
  },
}

// Composer Routes
export const compose = {
  route: '/compose',
}

export const newPost = {
  route: '/post/new',
}

// Deep link routes
export const linkToPost = {
  route: '/link-to-post',
  getRoute: ({
    profileId,
    postId,
    noteId,
  }: {
    profileId: string
    postId: string
    noteId?: string
  }): string =>
    `/link-to-post?profileId=${profileId}&postId=${postId}&noteId=${noteId}}`,
}

// Settings Routes
export const settings = {
  route: '/settings',
  getRoute: (): string => '/settings',
}

export const appsAndExtrasRoute = {
  route: '/settings/apps-extras',
}

export const settingsPreferencesRoute = {
  route: '/settings/preferences',
}

export const settingsNotificationsRoute = {
  route: '/settings/notifications',
}

export const generalSettingsRoute = {
  route: '/settings/general',
}

export const referAFriendRoute = {
  route: '/settings/refer-a-friend',
}

export const betaFeaturesRoute = {
  route: '/settings/beta',
}

export const settingsTeamRoute = {
  route: '/settings/team',
}

export const settingsRoleAndPermissionsRoute = {
  route: '/settings/role-and-permissions',
}

export const settingsChannelsRoute = {
  route: '/settings/channels',
}

export const settingsBillingRoute = {
  route: '/settings/billing',
}

export const settingsBillingKeepMyPlanRoute = {
  route: '/settings/billing/keep-my-plan',
}

export const settingsChannelsSettingsRoute = {
  route: '/settings/channels/:id',
}

export const settingsSavedRepliesRoute = {
  route: '/settings/saved-replies',
}

export const settingsChannelGroupsRoute = {
  route: '/settings/channel-groups',
}

export const settingsTagsRoute = {
  route: '/settings/tags',
}

export const settingsDeveloperRoute = {
  route: '/settings/api',
}

export const settingsImportRoute = {
  route: '/settings/import',
}

// Insights routes

/**
 * Period selection shared by both Insights views, so switching between them
 * keeps the range the user picked. Values are unrestricted: the selectors
 * (`useDateRangeSelector`, `useComparisonSelector`) already fall back to their
 * defaults on anything they can't parse.
 */
const insightsPeriodQueryParams = [
  { param: 'dateRange', values: undefined },
  { param: 'startDate', values: undefined },
  { param: 'endDate', values: undefined },
  { param: 'comparison', values: undefined },
  { param: 'comparisonStartDate', values: undefined },
  { param: 'comparisonEndDate', values: undefined },
] as const

export const insightsPage = {
  route: '/insights',
  queryParams: [
    ...insightsPeriodQueryParams,
    { param: 'tags', values: undefined },
    { param: 'channels', values: undefined },
  ] as const,
  buildPathname: (): string => '/insights',
}

export const insightsChannel = {
  route: '/insights/:channelId',
  postsRoute: '/insights/:channelId/posts/:postId',
  queryParams: [
    ...insightsPeriodQueryParams,
    { param: 'tags', values: undefined },
    // Carried to and from the Publish channel page, which uses the same param
    // name and the same values. Not registered on the all-channels view above,
    // where the filter has no meaning and the URL would claim one it ignores.
    { param: 'postTypes', values: undefined },
  ] as const,
  buildPathname: ({ channelId }: { channelId: string }): string =>
    `/insights/${channelId}`,
  getRoute: ({ channelId }: { channelId: string }): string =>
    `/insights/${channelId}`,
}
